import type { DisplayCue, Language, ProductInfo } from "@expert/contentful";

import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import type { EligibilityMeta, EligibilityStatus, EligibleProduct, SmartLinkMeta } from "../types";

import { getIsProd } from "../config";
import { SaleProgress } from "../types";
import { defaultEligibilityMeta, normalizeEligibilityMeta } from "./useProductOfferStore.helpers";

type ProductState = {
    products: EligibleProduct[];
    selectedProduct: EligibleProduct | undefined;
    displayCues?: DisplayCue[];
};

type SmartLinkState = Record<string, SmartLinkMeta>;
/** productOfferId -> failed `/v2/enrollment` attempts. Named (not inline) to match SmartLinkState
 *  and to keep the immer draft type from tripping TS2589 on the store interface. */
type EnrollmentAttemptState = Record<string, number>;

interface ProductOfferStore {
    productStateBySession: Record<string, ProductState>;
    eligibilityMetaBySession: Record<string, EligibilityMeta>;
    smartLinkMetaBySession: Record<string, SmartLinkState>;
    /**
     * Failed `/v2/enrollment` attempts per (session, productOfferId) — the budget behind the Enroll
     * attempt cap (ew#7570). Keyed by productOfferId, not sessionId alone, because a session can
     * carry several eligible primary products and a capped offer must not retire Enroll for an
     * unrelated one (same keying bug #7123 fixed in useOfferChecklistStore and #7412 fixed for
     * smart-link remainingAttempts). Lives here rather than in component state so the cap survives
     * the step unmounting and, via `partialize`, a reload — `useIntegratedFlowStore` persists the
     * step, so a local counter would come back empty and re-arm Enroll on the same customer.
     */
    failedEnrollmentAttemptsBySession: Record<string, EnrollmentAttemptState>;

    getProductState: (sessionId: string) => ProductState;
    getProducts: (sessionId: string) => EligibleProduct[];
    getPrimaryProducts: (sessionId: string) => EligibleProduct[];
    getOtherProducts: (sessionId: string) => EligibleProduct[];
    getSelectedProduct: (sessionId: string) => EligibleProduct | undefined;
    getSessionIdForProductId: (productId: string) => string | undefined;
    getEligibilityMetaBySessionId: (sessionId: string) => EligibilityMeta;
    getSmartLinkMetaBySessionId: (sessionId: string, productOfferId: string) => SmartLinkMeta;
    getFailedEnrollmentAttempts: (sessionId: string, productOfferId: string) => number;
    getDisplayCues: (sessionId: string) => DisplayCue[] | undefined;
    updateProducts: (sessionId: string, product: EligibleProduct[], displayCues?: DisplayCue[]) => void;
    updateProduct: (
        sessionId: string,
        productId: string,
        change: { saleProgress?: SaleProgress; status?: EligibilityStatus; isConfettiShown?: boolean },
    ) => void;
    updateProductsConfettiStatus: (sessionId: string) => void;
    updateSelectedProduct: (sessionId: string, product: EligibleProduct | undefined) => void;
    updateEligibilityMetaBySessionId: (
        sessionId: string,
        eligibilityMeta: EligibilityMeta | Partial<EligibilityMeta>,
    ) => void;
    updateSmartLinkMetaBySessionId: (sessionId: string, productOfferId: string, smartLinkMeta: SmartLinkMeta) => void;
    /** Records one failed enrollment attempt and returns the new total for that offer. */
    incrementFailedEnrollmentAttempts: (sessionId: string, productOfferId: string) => number;

    isPrimaryProductInProgress: (sessionId: string) => boolean;
    getProductInfo: (sessionId: string, sku?: string, lang?: Language) => ProductInfo | undefined;
    resetSaleSession: (sessionId: string) => void;
    endSaleSession: (sessionId: string) => void;
}

export const useProductOfferStore = createWithEqualityFn<ProductOfferStore>()(
    persist(
        immer(
            devtools(
                (set, get) => ({
                    productStateBySession: {},
                    eligibilityMetaBySession: {},
                    smartLinkMetaBySession: {},
                    failedEnrollmentAttemptsBySession: {},
                    updateProducts: (sessionId, products, displayCues) => {
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                const existingProducts = currentProductState.products;

                                // try to maintain sales progress state of the current products when setting new products
                                const updatedProducts = products.map((newProduct) => {
                                    // Match by id first, then fall back to productSku: ASP can return the
                                    // same offer (same SKU) under a new id on a re-check, and an id-only
                                    // match would drop the in-progress sale state onto the floor.
                                    const matchingExistingProduct =
                                        existingProducts.find(
                                            (existingProduct) => existingProduct.id === newProduct.id,
                                        ) ??
                                        existingProducts.find(
                                            (existingProduct) => existingProduct.productSku === newProduct.productSku,
                                        );

                                    // if no match, do not alter the incoming product
                                    if (!matchingExistingProduct) {
                                        return newProduct;
                                    }

                                    // if match, carry over the client-side sale state: sale progress and
                                    // whether confetti already fired (preserving saleProgress without
                                    // isConfettiShown would re-fire the sale confetti on every re-check).
                                    return {
                                        ...newProduct,
                                        saleProgress: matchingExistingProduct.saleProgress,
                                        isConfettiShown: matchingExistingProduct.isConfettiShown,
                                    };
                                });

                                (state.productStateBySession[sessionId] as ProductState) = {
                                    ...currentProductState,
                                    products: updatedProducts,
                                    displayCues,
                                };
                            },
                            false,
                            "updateProducts",
                        );
                    },
                    updateProduct: (sessionId, productId, change) => {
                        set(
                            (state) => {
                                const { products, selectedProduct } = state.getProductState(sessionId);
                                if (!products) {
                                    return;
                                }

                                const newPrimaryProducts = products.map((currentProduct) =>
                                    currentProduct.id === productId ? { ...currentProduct, ...change } : currentProduct,
                                );

                                const newSelectedProduct =
                                    selectedProduct?.id === productId
                                        ? { ...selectedProduct, ...change }
                                        : selectedProduct;

                                (state.productStateBySession[sessionId] as ProductState) = {
                                    ...state.productStateBySession[sessionId],
                                    products: newPrimaryProducts,
                                    selectedProduct: newSelectedProduct,
                                };
                            },
                            false,
                            "updateProduct",
                        );
                    },
                    updateProductsConfettiStatus: (sessionId) =>
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                const updatedProducts = currentProductState.products.map((product) => {
                                    if (product.saleProgress === SaleProgress.Success && !product.isConfettiShown) {
                                        return { ...product, isConfettiShown: true };
                                    }
                                    return product;
                                });
                                (state.productStateBySession[sessionId] as ProductState) = {
                                    ...currentProductState,
                                    products: updatedProducts,
                                };
                            },
                            false,
                            "updateProductsConfettiStatus",
                        ),
                    updateSelectedProduct: (sessionId, product) =>
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                (state.productStateBySession[sessionId] as ProductState) = {
                                    ...currentProductState,
                                    selectedProduct: product,
                                };
                            },
                            false,
                            "updateSelectedProduct",
                        ),
                    updateEligibilityMetaBySessionId: (sessionId, eligibilityMeta) => {
                        set(
                            (state) => {
                                state.eligibilityMetaBySession[sessionId] = normalizeEligibilityMeta(eligibilityMeta);
                            },
                            false,
                            "updateEligibilityMeta",
                        );
                    },
                    updateSmartLinkMetaBySessionId: (sessionId, productOfferId, smartLinkMeta) => {
                        set(
                            (state) => {
                                if (!state.smartLinkMetaBySession[sessionId]) {
                                    state.smartLinkMetaBySession[sessionId] = {};
                                }
                                state.smartLinkMetaBySession[sessionId][productOfferId] = smartLinkMeta;
                            },
                            false,
                            "updateSmartLinkMeta",
                        );
                    },
                    incrementFailedEnrollmentAttempts: (sessionId, productOfferId) => {
                        set(
                            (state) => {
                                if (!state.failedEnrollmentAttemptsBySession[sessionId]) {
                                    state.failedEnrollmentAttemptsBySession[sessionId] = {};
                                }
                                const bucket = state.failedEnrollmentAttemptsBySession[sessionId];
                                bucket[productOfferId] = (bucket[productOfferId] ?? 0) + 1;
                            },
                            false,
                            "incrementFailedEnrollmentAttempts",
                        );
                        // Read back the committed value rather than returning a locally-computed one, so
                        // the caller's "did this attempt hit the cap" decision can never disagree with
                        // what the store holds (zustand `set` is synchronous).
                        return get().getFailedEnrollmentAttempts(sessionId, productOfferId);
                    },
                    isPrimaryProductInProgress: (sessionId) => {
                        return (
                            get()
                                .getPrimaryProducts(sessionId)
                                ?.some((p) => p.saleProgress === SaleProgress.InProgress) ?? false
                        );
                    },
                    getProductState: (sessionId) => {
                        return (
                            get().productStateBySession[sessionId] ?? {
                                products: [],
                                selectedProduct: undefined,
                            }
                        );
                    },
                    getProducts: (sessionId) => {
                        return get().getProductState(sessionId).products;
                    },
                    getPrimaryProducts: (sessionId) =>
                        get()
                            .getProductState(sessionId)
                            .products.filter((p) => p.isPrimaryProduct) ?? [],
                    getOtherProducts: (sessionId) =>
                        get()
                            .getProductState(sessionId)
                            .products.filter((p) => !p.isPrimaryProduct) ?? [],
                    getSelectedProduct: (sessionId) => {
                        return get().getProductState(sessionId).selectedProduct;
                    },
                    getSessionIdForProductId: (productId) => {
                        return Object.keys(get().productStateBySession).find((sessionId) =>
                            get()
                                .getProductState(sessionId)
                                .products.some((product) => product.id === productId),
                        );
                    },
                    getEligibilityMetaBySessionId: (sessionId) => {
                        return get().eligibilityMetaBySession[sessionId] ?? { ...defaultEligibilityMeta };
                    },
                    getProductInfo: (sessionId, sku, lang = "en-US") => {
                        return get()
                            .productStateBySession[sessionId]?.products.filter((p) => p.productSku === sku)
                            .at(0)?.productInfo?.[lang];
                    },
                    getSmartLinkMetaBySessionId: (sessionId, productOfferId) => {
                        const sessionMeta = get().smartLinkMetaBySession[sessionId];
                        if (!sessionMeta) {
                            return { maximumSmartLinksGenerated: false };
                        }
                        return sessionMeta[productOfferId] ?? { maximumSmartLinksGenerated: false };
                    },
                    // Returns a primitive, so unlike getSmartLinkMetaBySessionId there is no
                    // empty-session referential-stability concern (CLAUDE.md > Referential Stability).
                    getFailedEnrollmentAttempts: (sessionId, productOfferId) =>
                        get().failedEnrollmentAttemptsBySession[sessionId]?.[productOfferId] ?? 0,
                    getDisplayCues: (sessionId) => {
                        return get().productStateBySession?.[sessionId]?.displayCues;
                    },
                    resetSaleSession: (sessionId) =>
                        set(
                            (state) => {
                                delete state.productStateBySession[sessionId];
                                delete state.smartLinkMetaBySession[sessionId];
                                delete state.failedEnrollmentAttemptsBySession[sessionId];
                            },
                            false,
                            "resetSaleSession",
                        ),
                    endSaleSession: (sessionId) =>
                        set(
                            (state) => {
                                delete state.productStateBySession[sessionId];
                                delete state.smartLinkMetaBySession[sessionId];
                                delete state.eligibilityMetaBySession[sessionId];
                                delete state.failedEnrollmentAttemptsBySession[sessionId];
                            },
                            false,
                            "endSaleSession",
                        ),
                }),
                {
                    enabled: !getIsProd(),
                    store: "product-offer-store",
                    name: "workspace/sales-tools",
                },
            ),
        ),
        {
            name: "product-offer-store",
            // Skipped, but manually called elsewhere: void useProductOfferStore.persist.rehydrate();
            skipHydration: true,
            partialize: (state) => ({
                productStateBySession: state.productStateBySession,
                eligibilityMetaBySession: state.eligibilityMetaBySession,
                smartLinkMetaBySession: state.smartLinkMetaBySession,
                failedEnrollmentAttemptsBySession: state.failedEnrollmentAttemptsBySession,
            }),
        },
    ),
    shallow,
);
